import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Plus, FileText, ChevronRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LanguageSelect } from "@/components/LocalePickers";

export default function Reports() {
  const { t } = useTranslation(["reports", "common", "errors"]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const canCreate = ["owner", "admin", "researcher"].includes(role);

  const [reports, setReports] = useState([]);
  const [brands, setBrands] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get("/reports"), api.get("/brands"), api.get("/research/projects")])
      .then(([r, b, p]) => {
        setReports(r.data);
        setBrands(b.data);
        setProjects(p.data);
      })
      .catch((err) => toast.error(formatApiError(err, t("errors:generic"))))
      .finally(() => setLoading(false));
  }, [t]);

  const brandById = useMemo(() => Object.fromEntries(brands.map((b) => [b.id, b])), [brands]);
  const projectById = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">{t("title")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("subtitle")}</p>
        </div>
        {canCreate && (
          <Button onClick={() => setOpen(true)} data-testid="reports-new-button">
            <Plus className="mr-2 h-4 w-4" /> {t("newReport")}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> {t("common:loading")}</div>
      ) : reports.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-16 text-center" data-testid="reports-empty">
          <FileText className="mx-auto h-10 w-10 text-slate-300" strokeWidth={1.5} />
          <p className="mt-3 text-sm text-slate-500">{t("empty")}</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("table.title")}</th>
                <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("table.brand")}</th>
                <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("table.project")}</th>
                <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("table.status")}</th>
                <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("table.language")}</th>
                <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("table.updated")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => {
                const proj = projectById[r.project_id];
                const brand = proj ? brandById[proj.brand_id] : null;
                return (
                  <tr key={r.id} data-testid={`report-row-${r.id}`}
                      onClick={() => navigate(`/reports/${r.id}`)}
                      className="cursor-pointer border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-6 py-4 font-medium text-slate-900">{r.title}</td>
                    <td className="px-6 py-4 text-slate-600">{brand?.name || "—"}</td>
                    <td className="px-6 py-4 text-slate-600">{proj?.name || "—"}</td>
                    <td className="px-6 py-4">
                      <Badge variant={r.status === "published" ? "default" : "outline"}>{t(`status.${r.status}`)}</Badge>
                    </td>
                    <td className="px-6 py-4 uppercase text-slate-500">{r.language}</td>
                    <td className="px-6 py-4 text-slate-500">{new Date(r.updated_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4 text-right text-slate-400"><ChevronRight className="ml-auto h-4 w-4" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ReportCreateDialog open={open} setOpen={setOpen} projects={projects} brandById={brandById} />
    </div>
  );
}

function ReportCreateDialog({ open, setOpen, projects, brandById }) {
  const { t } = useTranslation(["reports", "common", "errors"]);
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState("");
  const [runs, setRuns] = useState([]);
  const [runId, setRunId] = useState("");
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!projectId) { setRuns([]); setRunId(""); return; }
    api.get(`/research/projects/${projectId}/runs`).then(({ data }) => {
      const completed = data.filter((r) => r.status === "completed");
      setRuns(completed);
      if (completed.length) setRunId(completed[0].id);
      const proj = projects.find((p) => p.id === projectId);
      const brand = proj ? brandById[proj.brand_id] : null;
      if (brand && proj) {
        setLanguage(proj.language || "en");
        setTitle(`${brand.name} – ${proj.type.replace("_", " ")} – ${new Date().toISOString().slice(0,10)}`);
      }
    }).catch(() => {});
  }, [projectId, projects, brandById]);

  const submit = async () => {
    if (!projectId) return;
    setCreating(true);
    try {
      const { data } = await api.post("/reports", {
        project_id: projectId, run_id: runId || null,
        title: title.trim() || null, language: language || null,
      });
      setOpen(false);
      navigate(`/reports/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="report-create-dialog">
        <DialogHeader>
          <DialogTitle>{t("create.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t("create.selectProject")}</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="report-project-select"><SelectValue placeholder={t("create.selectProject")} /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {runs.length > 0 && (
            <div className="space-y-2">
              <Label>{t("create.selectRun")}</Label>
              <Select value={runId} onValueChange={setRunId}>
                <SelectTrigger data-testid="report-run-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {runs.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {new Date(r.created_at).toLocaleString()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label>{t("create.titleLabel")}</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="report-title-input" />
          </div>
          <div className="space-y-2">
            <Label>{t("create.languageLabel")}</Label>
            <LanguageSelect value={language} onChange={setLanguage} dataTestid="report-language-select" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>{t("create.cancel")}</Button>
          <Button onClick={submit} disabled={!projectId || creating} data-testid="report-create-submit">
            {creating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("create.creating")}</> : t("create.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
